import {isSessionState, TERMINAL_SESSION_STATES} from '../types/session'
import type {BaseTask} from '../types/task'
import type {HydratedAPITask} from '../types/api-task'
import {getAPITaskState} from '../utils/task-helpers'
import {resolveAgentProfile, resolveAgentProfileForAPITask} from './resolve-agent-profile'

/**
 * Interval at which to poll for fresh task data when interactive sessions are active.
 * Interactive sessions use `stale_at` which is computed at response time (not stored),
 * so Alive events never trigger updates for it — polling is required.
 */
export const INTERACTIVE_POLL_INTERVAL_MS = 20_000

/**
 * Returns the polling interval for interactive sessions when `shouldPoll` is true.
 * Returns `false` otherwise. Intended for use as a `refetchInterval` value.
 */
export function getInteractiveRefetchInterval(shouldPoll: boolean): number | false {
  return shouldPoll ? INTERACTIVE_POLL_INTERVAL_MS : false
}

/**
 * Returns true when any task in the list belongs to an interactive agent type.
 */
export function hasInteractiveTasks(tasks: Array<Pick<BaseTask, 'agentSlug'>>): boolean {
  return tasks.some(task => resolveAgentProfile(task.agentSlug).interactiveSession)
}

/**
 * Returns true when the given API task belongs to an interactive agent
 * and has not yet reached a terminal state.
 */
export function isNonTerminalInteractiveAPITask(task: HydratedAPITask | null | undefined): boolean {
  if (!task) return false
  const profile = resolveAgentProfileForAPITask(task)
  if (!profile.interactiveSession) return false
  const state = getAPITaskState(task)
  return !isSessionState(state) || !TERMINAL_SESSION_STATES.has(state)
}
